import { useCallback, useMemo, useState } from "react";
import companyApi from "../api/companyApi";
import useResourceWorkflow from "./useResourceWorkflow";
import {
  buildClassificationTableFromValues,
  createDefaultClassificationTable,
  normalizeClassificationTable,
} from "../components/companies/resourceUtils";
import {
  COMPANY_LIFECYCLE_STAGE_OPTIONS,
  VALID_STAGES_BY_LIFECYCLE,
} from "../constants/companyFields";
import {
  defaultResourceReportForm,
  defaultResourceReserveForm,
  defaultEconomicScenarioForm,
  defaultEconomicScenarioPriceRow,
} from "../components/companies/resourceDialogConstants";
import { toNumber } from "../utils/numberUtils";

export default function useResourceManagement({
  classificationOptions,
  resourceReservesByAsset,
  onResourceReserveSaved,
  onResourceReserveDeleted,
  onResourceReportSaved,
  onResourceReportDeleted,
  onEconomicScenarioDeleted,
  defaultReportingCurrency,
  assets,
}) {
  const [addResourceReserveOpen, setAddResourceReserveOpen] = useState(false);
  const [resourceReserveForm, setResourceReserveForm] = useState(defaultResourceReserveForm);
  const [resourceReserveSaveState, setResourceReserveSaveState] = useState({
    loading: false,
    error: null,
    success: null,
  });
  const [resourceReserveDialogMode, setResourceReserveDialogMode] = useState("create");
  const [resourceReserveDeleteTarget, setResourceReserveDeleteTarget] = useState(null);
  const [resourceReserveDeleteState, setResourceReserveDeleteState] = useState(null);
  const [addResourceReportOpen, setAddResourceReportOpen] = useState(false);
  const [resourceReportForm, setResourceReportForm] = useState(defaultResourceReportForm);
  const [resourceReportSaveState, setResourceReportSaveState] = useState({
    loading: false,
    error: null,
    success: null,
  });
  const [classificationTable, setClassificationTable] = useState(
    createDefaultClassificationTable(classificationOptions)
  );
  const [resourceReportDialogMode, setResourceReportDialogMode] = useState("create");
  const [resourceReportDeleteTarget, setResourceReportDeleteTarget] = useState(null);
  const [resourceReportDeleteState, setResourceReportDeleteState] = useState(null);
  const [resourceReportDuplicateNotice, setResourceReportDuplicateNotice] = useState(null);

  const [economicScenarioSaveState, setEconomicScenarioSaveState] = useState({
    loading: false,
    error: null,
    success: null,
  });
  const [economicScenarioDeleteTarget, setEconomicScenarioDeleteTarget] = useState(null);
  const [draftEconomicScenarios, setDraftEconomicScenarios] = useState([]);

  const stageOptions = useMemo(() => {
    const lifecycle = resourceReserveForm.lifecycle;
    const allowedStages = VALID_STAGES_BY_LIFECYCLE[lifecycle] || [];
    return COMPANY_LIFECYCLE_STAGE_OPTIONS.filter((opt) =>
      allowedStages.includes(opt.value)
    );
  }, [resourceReserveForm.lifecycle]);

  const assetCurrencyById = useMemo(() => {
    const map = new Map();
    (assets || []).forEach((asset) => {
      if (!asset?.id) return;
      map.set(asset.id, asset.reporting_currency);
    });
    return map;
  }, [assets]);

  const assetById = useMemo(() => {
    const map = new Map();
    (assets || []).forEach((asset) => {
      if (!asset?.id) return;
      map.set(asset.id, asset);
    });
    return map;
  }, [assets]);

  const assetCurrencyByReserveId = useMemo(() => {
    const map = new Map();
    Object.entries(resourceReservesByAsset || {}).forEach(([assetId, reserves]) => {
      const resolvedAssetId = Number(assetId);
      const assetCurrency =
        assetCurrencyById.get(resolvedAssetId) || defaultReportingCurrency || "USD";
      (reserves || []).forEach((reserve) => {
        if (!reserve?.id) return;
        map.set(reserve.id, assetCurrency);
      });
    });
    return map;
  }, [assetCurrencyById, defaultReportingCurrency, resourceReservesByAsset]);

  const resolveCurrencyForReserve = useCallback(
    (reserveId) =>
      assetCurrencyByReserveId.get(reserveId) || defaultReportingCurrency || "USD",
    [assetCurrencyByReserveId, defaultReportingCurrency]
  );

  const handleResourceReserveFieldChange = (field) => (event) => {
    const value = event.target.value;
    setResourceReserveForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "lifecycle" ? { lifecycle_stage: "" } : null),
    }));
  };

  const handleResourceReportFieldChange = (field) => (event) => {
    const value =
      event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setResourceReportForm((prev) => ({ ...prev, [field]: value }));
  };


  const handleOpenAddResourceReserve = (assetId) => {
    const asset = assetById.get(assetId) || {};
    setResourceReserveForm({
      ...defaultResourceReserveForm,
      reserve_id: "",
      asset_id: assetId,
      lifecycle: asset.lifecycle || defaultResourceReserveForm.lifecycle,
      lifecycle_stage: asset.lifecycle_stage || "",
      start_year_of_operation: asset.start_year_of_operation ?? "",
      expected_first_production_year: asset.expected_first_production_year ?? "",
      closure_or_reclamation_year: asset.closure_or_reclamation_year ?? "",
    });
    setResourceReserveSaveState({ loading: false, error: null, success: null });
    setResourceReserveDialogMode("create");
    setAddResourceReserveOpen(true);
  };

  const handleOpenEditResourceReserve = (reserve) => {
    if (!reserve) return;
    const assetId = reserve?.asset?.id || reserve?.asset_id || reserve?.asset || "";
    setResourceReserveForm({
      reserve_id: reserve.id,
      asset_id: assetId,
      name: reserve.name || "",
      mining_method: reserve.mining_method || "",
      lifecycle: reserve.lifecycle || "na",
      lifecycle_stage: reserve.lifecycle_stage || "",
      start_year_of_operation: reserve.start_year_of_operation ?? "",
      expected_first_production_year: reserve.expected_first_production_year ?? "",
      closure_or_reclamation_year: reserve.closure_or_reclamation_year ?? "",
      notes: reserve.notes || "",
    });
    setResourceReserveSaveState({ loading: false, error: null, success: null });
    setResourceReserveDialogMode("edit");
    setAddResourceReserveOpen(true);
  };

  const handleDuplicateResourceReserve = (reserve) => {
    if (!reserve) return;
    const assetId = reserve?.asset?.id || reserve?.asset_id || reserve?.asset || "";
    setResourceReserveForm({
      reserve_id: "",
      asset_id: assetId,
      name: "",
      mining_method: reserve.mining_method || "",
      lifecycle: reserve.lifecycle || "na",
      lifecycle_stage: reserve.lifecycle_stage || "",
      start_year_of_operation: reserve.start_year_of_operation ?? "",
      expected_first_production_year: reserve.expected_first_production_year ?? "",
      closure_or_reclamation_year: reserve.closure_or_reclamation_year ?? "",
      notes: reserve.notes || "",
    });
    setResourceReserveSaveState({ loading: false, error: null, success: null });
    setResourceReserveDialogMode("create");
    setAddResourceReserveOpen(true);
  };

  const handleCloseResourceReserveDialog = () => {
    setAddResourceReserveOpen(false);
    setResourceReserveDialogMode("create");
    setResourceReserveForm(defaultResourceReserveForm);
  };

  const handleOpenAddResourceReport = (resourceReserveId) => {
    setResourceReportForm({
      ...defaultResourceReportForm,
      report_id: "",
      resource_reserve_id: resourceReserveId,
    });
    setClassificationTable(createDefaultClassificationTable(classificationOptions));
    setResourceReportSaveState({ loading: false, error: null, success: null });
    setResourceReportDialogMode("create");
    setDraftEconomicScenarios([]);
    setResourceReportDuplicateNotice(null);
    setAddResourceReportOpen(true);
  };

  const handleOpenEditResourceReport = (report, reserveId) => {
    if (!report) return;
    setResourceReportDuplicateNotice(null);
    const resolvedCurrency = resolveCurrencyForReserve(reserveId);
    setResourceReportForm({
      report_id: report.id,
      resource_reserve_id: reserveId,
      as_of_date: report.period?.as_of_date || report.as_of_date || "",
      estimate_period_unknown: Boolean(report.estimate_period_unknown),
      source_title: report.source_title || "",
      source_url: report.source_url || "",
      source_notes: report.source_notes || "",
    });
    setClassificationTable(
      buildClassificationTableFromValues(report.values || [], classificationOptions)
    );
    setResourceReportSaveState({ loading: false, error: null, success: null });
    setResourceReportDialogMode("edit");
    const existingScenarios = (report.economic_scenarios || []).map((scenario) => {
      const prices = (scenario.prices || []).map((price) => ({
        commodity_id:
          price.commodity?.id ||
          price.commodity_id ||
          price.commodity_detail?.id ||
          price.commodity ||
          "",
        price: price.price ?? "",
      }));
      return {
        ...defaultEconomicScenarioForm,
        temp_id: `draft-${scenario.id}`,
        scenario_id: scenario.id,
        report_id: report.id,
        label: scenario.label || "",
        tax_treatment: scenario.tax_treatment || "",
        discount_rate: scenario.discount_rate ?? "",
        irr_value: scenario.irr_value ?? "",
        payback_period: scenario.payback_period ?? "",
        currency: scenario.currency || resolvedCurrency,
        npv_value: scenario.npv_value ?? "",
        notes: scenario.notes || "",
        prices: prices.length ? prices : [{ ...defaultEconomicScenarioPriceRow }],
      };
    });
    setDraftEconomicScenarios(existingScenarios);
    setAddResourceReportOpen(true);
  };

  const handleDuplicateResourceReport = (report, reserveId) => {
    if (!report) return;
    const resolvedCurrency = resolveCurrencyForReserve(reserveId);
    setResourceReportForm({
      report_id: "",
      resource_reserve_id: reserveId,
      as_of_date: "",
      estimate_period_unknown: Boolean(report.estimate_period_unknown),
      source_title: "",
      source_url: "",
      source_notes: "",
    });
    setClassificationTable(
      buildClassificationTableFromValues(report.values || [], classificationOptions)
    );
    const duplicatedScenarios = (report.economic_scenarios || []).map((scenario) => {
      const prices = (scenario.prices || []).map((price) => ({
        commodity_id:
          price.commodity?.id ||
          price.commodity_id ||
          price.commodity_detail?.id ||
          price.commodity ||
          "",
        price: price.price ?? "",
      }));
      return {
        ...defaultEconomicScenarioForm,
        temp_id: `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        scenario_id: "",
        report_id: "",
        label: scenario.label || "",
        tax_treatment: scenario.tax_treatment || "",
        discount_rate: scenario.discount_rate ?? "",
        irr_value: scenario.irr_value ?? "",
        payback_period: scenario.payback_period ?? "",
        currency: scenario.currency || resolvedCurrency,
        npv_value: scenario.npv_value ?? "",
        notes: scenario.notes || "",
        prices: prices.length ? prices : [{ ...defaultEconomicScenarioPriceRow }],
      };
    });
    setDraftEconomicScenarios(duplicatedScenarios);
    setResourceReportSaveState({ loading: false, error: null, success: null });
    setResourceReportDialogMode("create");
    setResourceReportDuplicateNotice(
      "Report copied. Save to create a new draft."
    );
    setAddResourceReportOpen(true);
  };

  const handleCloseResourceReportDialog = () => {
    setAddResourceReportOpen(false);
    setResourceReportDialogMode("create");
    setResourceReportForm(defaultResourceReportForm);
    setResourceReportDuplicateNotice(null);
  };


  const handleCommoditySelectionChange = (event) => {
    const value = event.target.value;
    const rawIds = Array.isArray(value) ? value : `${value}`.split(",");
    const nextCommodityIds = rawIds.map((id) => {
      const asNumber = Number(id);
      return Number.isNaN(asNumber) ? id : asNumber;
    });
    setClassificationTable((prev) =>
      normalizeClassificationTable(
        { ...prev, commodity_ids: nextCommodityIds },
        nextCommodityIds
      )
    );
  };

  const handleClassificationRowChange =
    (rowIndex, field, commodityId = null) =>
    (event) => {
      const value = event.target.value;
      setClassificationTable((prev) => {
        const rows = prev.rows.map((row, idx) => {
          if (idx !== rowIndex) return row;
          if (field === "tonnage_mt") {
            return { ...row, tonnage_mt: value };
          }
          const commodityKey = commodityId !== null ? `${commodityId}` : "";
          if (field === "grade_value") {
            return {
              ...row,
              grade_values: {
                ...(row.grade_values || {}),
                [commodityKey]: value,
              },
            };
          }
          if (field === "contained_value") {
            return {
              ...row,
              contained_values: {
                ...(row.contained_values || {}),
                [commodityKey]: value,
              },
            };
          }
          return row;
        });
        return { ...prev, rows };
      });
    };

  const handleSubmitResourceReserve = useCallback(async () => {
    if (!resourceReserveForm.asset_id) return;
    if (!resourceReserveForm.lifecycle) {
      setResourceReserveSaveState({
        loading: false,
        error: "Lifecycle is required.",
        success: null,
      });
      return;
    }
    if (!resourceReserveForm.lifecycle_stage) {
      setResourceReserveSaveState({
        loading: false,
        error: "Lifecycle stage is required.",
        success: null,
      });
      return;
    }
    setResourceReserveSaveState({ loading: true, error: null, success: null });
    try {
      const payload = {
        asset: resourceReserveForm.asset_id,
        name: resourceReserveForm.name || "",
        mining_method: resourceReserveForm.mining_method || null,
        lifecycle: resourceReserveForm.lifecycle,
        lifecycle_stage: resourceReserveForm.lifecycle_stage || null,
        start_year_of_operation: toNumber(resourceReserveForm.start_year_of_operation),
        expected_first_production_year: toNumber(
          resourceReserveForm.expected_first_production_year
        ),
        closure_or_reclamation_year: toNumber(resourceReserveForm.closure_or_reclamation_year),
        notes: resourceReserveForm.notes || "",
      };
      const reserveId = resourceReserveForm.reserve_id;
      const created = reserveId
        ? await companyApi.updateResourceReserve(reserveId, payload)
        : await companyApi.createResourceReserve(payload);
      if (onResourceReserveSaved) {
        onResourceReserveSaved(created);
      }
      setResourceReserveSaveState({
        loading: false,
        error: null,
        success: reserveId ? "Resource updated." : "Resource saved as draft.",
      });
      setAddResourceReserveOpen(false);
      setResourceReserveDialogMode("create");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.lifecycle_stage ||
        "Unable to save resource.";
      setResourceReserveSaveState({ loading: false, error: detail, success: null });
    }
  }, [resourceReserveForm, onResourceReserveSaved]);

  const handleConfirmDeleteResourceReserve = useCallback(async () => {
    if (!resourceReserveDeleteTarget?.reserveId) return;
    try {
      const result = await companyApi.deleteResourceReserve(resourceReserveDeleteTarget.reserveId);
      setResourceReserveDeleteTarget(null);
      setResourceReserveDeleteState(
        result?.status === "delete_requested"
          ? "Resource reserve deletion requested."
          : "Resource reserve deleted."
      );
      if (onResourceReserveDeleted) {
        onResourceReserveDeleted(
          resourceReserveDeleteTarget.reserveId,
          resourceReserveDeleteTarget.assetId
        );
      }
    } catch {
      setResourceReserveDeleteTarget(null);
      setResourceReserveDeleteState("Unable to delete resource reserve.");
    }
  }, [onResourceReserveDeleted, resourceReserveDeleteTarget]);

  const validateScenarioForReportSave = useCallback(() => {
    if (!draftEconomicScenarios.length) return null;
    for (const scenario of draftEconomicScenarios) {
      const currency = (scenario.currency || "USD").trim();
      if (!currency) {
        return "Currency is required for each economic scenario.";
      }
      if (scenario.npv_value === "" || scenario.npv_value === null) {
        return "NPV value is required for each economic scenario.";
      }
      const priceRows = (scenario.prices || []).filter(
        (row) => row.commodity_id || `${row.price}`.trim()
      );
      if (!priceRows.length) {
        return "Add at least one commodity price assumption per scenario.";
      }
      const seen = new Set();
      for (const row of priceRows) {
        if (!row.commodity_id) {
          return "Each price row requires a commodity.";
        }
        if (`${row.price}`.trim() === "") {
          return "Each price row requires a price value.";
        }
        if (seen.has(row.commodity_id)) {
          return "Each commodity can only appear once per scenario.";
        }
        seen.add(row.commodity_id);
      }
    }
    return null;
  }, [draftEconomicScenarios]);

  const buildEconomicScenarioPayloads = useCallback(() => {
    if (!draftEconomicScenarios.length) return [];
    return draftEconomicScenarios.map((scenario) => ({
      id: scenario.scenario_id,
      label: scenario.label || "",
      tax_treatment: scenario.tax_treatment || "",
      discount_rate: toNumber(scenario.discount_rate),
      irr_value: toNumber(scenario.irr_value),
      payback_period: toNumber(scenario.payback_period),
      currency: scenario.currency || "USD",
      npv_value: toNumber(scenario.npv_value),
      notes: scenario.notes || "",
      prices: (scenario.prices || [])
        .filter((row) => row.commodity_id)
        .map((row) => ({
          commodity: row.commodity_id,
          price: toNumber(row.price),
          unit: "",
        })),
    }));
  }, [draftEconomicScenarios]);

  const { submitResourceReport, updateResourceReport } = useResourceWorkflow({
    companyApi,
    classificationTable,
    resourceReportForm,
    economicScenarios: buildEconomicScenarioPayloads(),
    setResourceReportSaveState,
    setAddResourceReportOpen,
    setResourceReportDialogMode,
    onResourceReportSaved,
  });

  const handleSubmitResourceReport = useCallback(async () => {
    const scenarioError = validateScenarioForReportSave();
    if (scenarioError) {
      setEconomicScenarioSaveState({ loading: false, error: scenarioError, success: null });
      setResourceReportSaveState({ loading: false, error: scenarioError, success: null });
      return;
    }
    setEconomicScenarioSaveState({ loading: false, error: null, success: null });
    await submitResourceReport();
  }, [submitResourceReport, validateScenarioForReportSave]);

  const handleUpdateResourceReport = useCallback(async () => {
    const scenarioError = validateScenarioForReportSave();
    if (scenarioError) {
      setEconomicScenarioSaveState({ loading: false, error: scenarioError, success: null });
      setResourceReportSaveState({ loading: false, error: scenarioError, success: null });
      return;
    }
    setEconomicScenarioSaveState({ loading: false, error: null, success: null });
    await updateResourceReport();
  }, [updateResourceReport, validateScenarioForReportSave]);

  const handleConfirmDeleteResourceReport = useCallback(async () => {
    if (!resourceReportDeleteTarget?.reportId) return;
    setResourceReportDeleteState(null);
    try {
      const result = await companyApi.deleteResourceReserveReport(resourceReportDeleteTarget.reportId);
      setResourceReportDeleteTarget(null);
      if (resourceReportDeleteTarget.reserveId && onResourceReportDeleted) {
        onResourceReportDeleted(
          resourceReportDeleteTarget.reserveId,
          resourceReportDeleteTarget.reportId
        );
      }
      setResourceReportDeleteState(
        result?.status === "delete_requested"
          ? "Resource estimate deletion requested."
          : "Resource estimate deleted."
      );
    } catch {
      setResourceReportDeleteTarget(null);
      setResourceReportDeleteState("Unable to delete resource estimate.");
    }
  }, [resourceReportDeleteTarget, onResourceReportDeleted]);

  const handleConfirmDeleteEconomicScenario = useCallback(async () => {
    if (!economicScenarioDeleteTarget?.scenarioId) return;
    if (economicScenarioDeleteTarget.isDraft) {
      setDraftEconomicScenarios((prev) =>
        prev.filter((item) => item.temp_id !== economicScenarioDeleteTarget.scenarioId)
      );
      setEconomicScenarioDeleteTarget(null);
      return;
    }
    try {
      await companyApi.deleteResourceReserveEconomicScenario(
        economicScenarioDeleteTarget.scenarioId
      );
      setEconomicScenarioDeleteTarget(null);
      if (onEconomicScenarioDeleted) {
        onEconomicScenarioDeleted(economicScenarioDeleteTarget.scenarioId);
      }
    } catch {
      setEconomicScenarioDeleteTarget(null);
    }
  }, [economicScenarioDeleteTarget, onEconomicScenarioDeleted]);

  const handleDraftScenarioFieldChange = useCallback((tempId, field, value) => {
    setDraftEconomicScenarios((prev) =>
      prev.map((scenario) =>
        scenario.temp_id === tempId ? { ...scenario, [field]: value } : scenario
      )
    );
  }, []);

  const handleDraftScenarioPriceChange = useCallback((tempId, index, field, value) => {
    setDraftEconomicScenarios((prev) =>
      prev.map((scenario) => {
        if (scenario.temp_id !== tempId) return scenario;
        const prices = scenario.prices || [];
        const nextPrices = prices.map((row, idx) =>
          idx === index ? { ...row, [field]: value } : row
        );
        return { ...scenario, prices: nextPrices };
      })
    );
  }, []);

  const handleAddDraftScenarioPriceRow = useCallback((tempId) => {
    setDraftEconomicScenarios((prev) =>
      prev.map((scenario) => {
        if (scenario.temp_id !== tempId) return scenario;
        const prices = scenario.prices || [];
        return { ...scenario, prices: [...prices, { ...defaultEconomicScenarioPriceRow }] };
      })
    );
  }, []);

  const handleRemoveDraftScenarioPriceRow = useCallback((tempId, index) => {
    setDraftEconomicScenarios((prev) =>
      prev.map((scenario) => {
        if (scenario.temp_id !== tempId) return scenario;
        const prices = (scenario.prices || []).filter((_, idx) => idx !== index);
        return { ...scenario, prices };
      })
    );
  }, []);

  const handleRemoveDraftScenario = useCallback(
    async (tempId) => {
      const scenario = draftEconomicScenarios.find((item) => item.temp_id === tempId);
      if (scenario?.scenario_id) {
        try {
          await companyApi.deleteResourceReserveEconomicScenario(scenario.scenario_id);
        } catch {
          setEconomicScenarioSaveState({
            loading: false,
            error: "Unable to delete economic scenario.",
            success: null,
          });
          return;
        }
      }
      setDraftEconomicScenarios((prev) => prev.filter((item) => item.temp_id !== tempId));
    },
    [draftEconomicScenarios]
  );

  const handleOpenAddEconomicScenario = (report, reserveId) => {
    const resolvedCurrency = resolveCurrencyForReserve(reserveId);
    const tempId = `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const draftScenario = {
      ...defaultEconomicScenarioForm,
      report: report?.id || null,
      report_id: report?.id || "",
      temp_id: tempId,
      currency: resolvedCurrency,
      prices: [{ ...defaultEconomicScenarioPriceRow }],
    };
    setDraftEconomicScenarios((prev) => [...prev, draftScenario]);
    setEconomicScenarioSaveState({ loading: false, error: null, success: null });
  };

  const handleOpenEditEconomicScenario = (scenario, reserveId) => {
    if (!scenario) return;
    const resolvedCurrency = resolveCurrencyForReserve(reserveId);
    const resolveCommodityId = (commodity) => {
      if (!commodity) return "";
      if (typeof commodity === "object") {
        return commodity.id || "";
      }
      return commodity;
    };
    const tempId = `draft-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const prices = (scenario.prices || []).map((price) => ({
      commodity_id:
        resolveCommodityId(price.commodity) ||
        resolveCommodityId(price.commodity_id) ||
        resolveCommodityId(price.commodity_detail) ||
        "",
      price: price.price ?? "",
    }));
    setDraftEconomicScenarios((prev) => [
      ...prev,
      {
        ...defaultEconomicScenarioForm,
        temp_id: tempId,
        scenario_id: scenario.id,
        report_id: scenario.report || scenario.report_id || "",
        label: scenario.label || "",
        tax_treatment: scenario.tax_treatment || "",
        discount_rate: scenario.discount_rate ?? "",
        irr_value: scenario.irr_value ?? "",
        payback_period: scenario.payback_period ?? "",
        currency: scenario.currency || resolvedCurrency,
        npv_value: scenario.npv_value ?? "",
        notes: scenario.notes || "",
        prices: prices.length ? prices : [{ ...defaultEconomicScenarioPriceRow }],
      },
    ]);
    setEconomicScenarioSaveState({ loading: false, error: null, success: null });
  };

  return {
    addResourceReserveOpen,
    resourceReserveForm,
    resourceReserveSaveState,
    resourceReserveDialogMode,
    resourceReserveDeleteTarget,
    resourceReserveDeleteState,
    addResourceReportOpen,
    resourceReportForm,
    resourceReportSaveState,
    classificationTable,
    resourceReportDialogMode,
    resourceReportDeleteTarget,
    resourceReportDeleteState,
    resourceReportDuplicateNotice,
    economicScenarioSaveState,
    economicScenarioDeleteTarget,
    draftEconomicScenarios,
    stageOptions,
    setResourceReportDeleteTarget,
    setEconomicScenarioDeleteTarget,
    setAddResourceReserveOpen,
    setResourceReserveDeleteTarget,
    handleResourceReserveFieldChange,
    handleResourceReportFieldChange,
    handleOpenAddResourceReserve,
    handleOpenEditResourceReserve,
    handleDuplicateResourceReserve,
    handleCloseResourceReserveDialog,
    handleOpenAddResourceReport,
    handleOpenEditResourceReport,
    handleDuplicateResourceReport,
    handleCloseResourceReportDialog,
    handleCommoditySelectionChange,
    handleClassificationRowChange,
    handleSubmitResourceReserve,
    handleUpdateResourceReserve: handleSubmitResourceReserve,
    handleConfirmDeleteResourceReserve,
    handleSubmitResourceReport,
    handleUpdateResourceReport,
    handleConfirmDeleteResourceReport,
    handleOpenAddEconomicScenario,
    handleOpenEditEconomicScenario,
    handleConfirmDeleteEconomicScenario,
    handleDraftScenarioFieldChange,
    handleDraftScenarioPriceChange,
    handleAddDraftScenarioPriceRow,
    handleRemoveDraftScenarioPriceRow,
    handleRemoveDraftScenario,
  };
}
